import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useParams} from 'react-router-dom';

import SearchWidget from 'common/components/SearchWidget';
import {
	Button,
	CloseIcon,
	Dialog,
	IconButton,
	Layout,
	Page,
	ReloadIcon,
	SearchIcon,
	Text,
	Tooltip,
} from '@phonectas/unifon-ui-kit';
import {
	StyledCustomerGrid,
	StyledEventsTab,
	StyledGridItemCustomer,
	StyledGridItemHeader,
	StyledGridItemRight,
	StyledGridItemTables,
	StyledSectionDiv,
} from './styled';
import Portal from 'common/components/Portal';
import {LIMIT_PAYLOAD} from 'common/constants';
import useBoundingClientRect from 'common/hooks/useBoundingClientRect';
import {useSmallScreen} from 'common/hooks/useScreenSize';
import useThreeSixtyNewStore from 'common/stores/threeSixtyStore';
import {shallow} from 'zustand/shallow';
import CustomerTabs from './CustomerTabs/CustomerTabs';
import ThreeSixtySearchBar from '../ThreeSixtySearchBar';
import CustomerTabsNav from './CustomerTabs/CustomerTabsNav';
import EventsTab from './SmsAndEvents';
import CustomerDetails from './CustomerDetails';
import CustomerWarnings from './CustomerWarnings';
import Skeleton from 'common/components/Skeleton';
import PageLoader from './PageLoader';
// import SimpleZendeskIcon from 'common/components/icons/SimpleZendeskIcon';
import CreateZendeskContent from './CustomerTabs/tables/dialogContent/CreateZendeskContent';

interface FetchParams {
    id: string;
    limit?: number;
    search?: string;
    statuses?: boolean;
    lazy?: boolean;
    [key: string]: unknown;
}

type FetchFunction = (params: FetchParams) => void;

const ThreeSixtyCustomer: React.FC = () => {
	const [t] = useTranslation();
	const params = useParams();
	const isSmallScreen = useSmallScreen('(max-width:1600px)');
	const isSmallScreenHeight = useSmallScreen('(max-height: 800px)');
	// Only what the page itself renders: loading is read by PageLoader, and the templates only need to exist
	const {
		activeAccount,
		hasConfigTemplates,
	} = useThreeSixtyNewStore((state) => ({
		activeAccount: state.activeAccount,
		hasConfigTemplates: state.configTemplates !== null,
	}), shallow);
	const {
		getUsers,
		getContacts,
		getBillingGroups,
		getVismaList: getVismaListNew,
		getZendesk,
		getAccount,
		getConfigTemplates: getConfigTemplatesNew,
	} = useThreeSixtyNewStore((state) => ({
		getUsers: state.getUsers,
		getContacts: state.getContacts,
		getBillingGroups: state.getBillingGroups,
		getVismaList: state.getVismaList,
		getZendesk: state.getZendesk,
		getAccount: state.getAccount,
		getConfigTemplates: state.getConfigTemplates,
	}), shallow);
	const [customerId, setCustomerId] = useState<string | null>(params?.['id'] ?? null);
	const [showSearch, setShowSearch] = useState<boolean>(false);
	const [showTableSearch, setTableShowSearch] = useState<boolean>(false);
	const [tableQuery, setTableQuery] = useState<string>('');
	const [warnings, setWarnings] = useState<boolean>(false);
	const [activeTab, setActiveTab] = useState<string>('1');
	const [showZendeskDialog, setShowZendeskDialog] = useState<boolean>(false);
	const [sectionRef, boundingRect] = useBoundingClientRect<HTMLDivElement>();
	const [customerTabRef, customerTabRect] = useBoundingClientRect<HTMLDivElement>();
	const [expandEvent, setExpandEvent] = useState<boolean>(false);
	const [userTableFilter, setUserTableFilter] = useState<boolean>(true);
	const isSmallScreenRef = isSmallScreen && boundingRect;
	const onHideExpand = useCallback(() => {
		setExpandEvent(false);
	}, []);
	const onShowExpand = useCallback(() => {
		setExpandEvent(true);
	}, []);
	const onSearchClick = useCallback(() => setShowSearch((prev) => !prev), []);
	const fetchFunctions: Record<string, FetchFunction> = useMemo(() => ({
		'1': getUsers,
		'2': getBillingGroups,
		'3': getContacts,
		'4': getVismaListNew,
		'5': getZendesk,
		// '6': fetchBedriftsnett,
	}), [getUsers, getBillingGroups, getContacts, getZendesk, getVismaListNew]);
	const handleTableTabChange = useCallback((tab: string) => {
		setActiveTab(tab);
		setTableShowSearch(false);
	}, []);

	const fetchFunction = fetchFunctions[activeTab];
	const onTableQueryChange = useCallback((text: string) => {
		if (!text) return;
		if (!fetchFunction) return;
		if (!customerId) return;
		if (activeTab === '1') {
			setUserTableFilter(false);
			fetchFunction({
				limit: LIMIT_PAYLOAD,
				id: customerId ?? null,
				search: text,
				statuses: false,
			});
			setTableQuery(text);
			return;
		}
		if (activeTab === '4') {
			// getVismaList(customerId);
			return;
		}

		fetchFunction({
			limit: LIMIT_PAYLOAD,
			id: customerId ?? null,
			search: text,
			// filter: activeTab === '1' ? userTableFilter : undefined,
		});
		setTableQuery(text);
	}, [customerId, fetchFunction, setTableQuery, activeTab]);
	const onShowTableSearch = useCallback(() => setTableShowSearch(true), []);
	const onHideTableSearch = useCallback(() => {
		setTableShowSearch(false);
		setTableQuery('');
		// if the user is on the users tab, we want to reset the filter
		setUserTableFilter(true);
		if (fetchFunction) {
			if (!customerId) return;
			fetchFunction({
				limit: LIMIT_PAYLOAD,
				id: customerId ?? null,
			});
		}
	}, [customerId, fetchFunction]);

	const refreshTable = useCallback(() => {
		if (fetchFunction) {
			if (!customerId) return;
			fetchFunction({
				limit: LIMIT_PAYLOAD,
				id: customerId,
			});
		}
	}, [fetchFunction, customerId]);
	const onTableClearSearch = useCallback(() => setTableQuery(''), []);
	const toggleWarnings = () => setWarnings(!warnings);
	useEffect(() => {
		if (customerId) {
			getAccount({id: customerId, phone: null});
		}
	}, [customerId, getAccount]);

	// Separate from the account effect, so the templates arriving does not fetch the account again
	useEffect(() => {
		if (!hasConfigTemplates) {
			getConfigTemplatesNew('utland_kategorier');
		}
	}, [hasConfigTemplates, getConfigTemplatesNew]);

	useEffect(() => {
		if (customerId !== params?.['id']) {
			setCustomerId(params?.['id'] ?? null);
			setActiveTab('1');
			setUserTableFilter(true);
			if (showTableSearch) {
				onHideTableSearch();
			}
		}
	}, [params, customerId, setCustomerId, setActiveTab, showTableSearch, onHideTableSearch]);

	useEffect(() => {
		if (!customerId) return;
		if (activeAccount?.id !== customerId) {
			
			getContacts({
				limit: LIMIT_PAYLOAD,
				id: customerId,
				lazy: true,
				search: 'kontaktperson',
			});
			return;
		}
	}, [customerId, activeAccount?.id, getContacts]);

	// for test only warnings not implemented
	const testWarnings = false;

	return (
		<Page>
			<StyledCustomerGrid>
				<StyledGridItemHeader>
					<Layout direction="row" alignItems="center" nowrap fullWidth>
						<Text style={{fontSize: '30px'}} variant="h1">Unifon 360</Text>
						<div id="search_Customer_Portal" />
						<Portal portalId="search_Customer_Portal">
							<Layout style={{width: '400px'}} nowrap alignItems="center">
								{
									showSearch ?
										(
											<>
												<ThreeSixtySearchBar closeSearch={showSearch}/>
												<div>
													<IconButton onClick={onSearchClick}>
														<CloseIcon />
													</IconButton>
												</div>
											</>
										) :
										(
											<Button onClick={onSearchClick} icon={<SearchIcon />} compact>
												{t('Search')}
											</Button>
										)
								}
							</Layout>
						</Portal>
						{
							testWarnings && (
								<button style={{width: '135px'}} onClick={toggleWarnings}>{`Warnings: ${warnings}`}</button>
							)
						}
					</Layout>
				</StyledGridItemHeader>
				<StyledGridItemCustomer ref={customerTabRef}>
					<StyledSectionDiv $noPadding>
						{
							activeAccount ? (
								<CustomerDetails data={activeAccount} />
							) : <Skeleton $dark $height={isSmallScreenRef ? customerTabRect.height : 521} />
						}
						
					</StyledSectionDiv>
				</StyledGridItemCustomer>
				<StyledGridItemTables>
					<StyledSectionDiv
						$noBg
						style={{
							display: 'flex',
							justifyContent: 'space-between',
							alignItems: 'center',
							height: '60px',
						}}
					>
						<CustomerTabsNav
							activeTab={activeTab}
							setActiveTab={handleTableTabChange}
						/>
						<div id="search_Table_Portal" />
						<Portal portalId="search_Table_Portal">
							<Layout nowrap alignItems="center">
								{
									showTableSearch ?
										(
											<>
												<SearchWidget
													onChange={onTableQueryChange}
													placeholder={t('Search')}
													// openSearch={onShowTableSearch}
													value={tableQuery}
													searchQuery={tableQuery}
													clearSearch={onTableClearSearch}
													autoFocus
												/>
												<div>
													<IconButton onClick={onHideTableSearch}>
														<CloseIcon />
													</IconButton>
												</div>
											</>
										) :
										(
											<>
												{/* {
													activeTab === '5' && (
														
															<IconButton
																onClick={() => setShowZendeskDialog(true)}
																size='sm'
															>
																<span style={{width: '20px'}}>
																<SimpleZendeskIcon color="#001B74" />
																</span>
															</IconButton>
														
													)
												} */}
												<Tooltip text={t('Force refresh of current tab')}>
													<IconButton
														size="xl"
														onClick={refreshTable}
													>
														<ReloadIcon />
													</IconButton>
												</Tooltip>
												<Button
													onClick={onShowTableSearch}
													icon={<SearchIcon />}
													compact
												>
													{t('Search')}
												</Button>
											</>
										)
								}
							</Layout>
						</Portal>
					</StyledSectionDiv>
					<CustomerTabs
						customerId={customerId ?? ''}
						activeTab={activeTab}
						userTableFilter={userTableFilter}
						setUserTableFilter={setUserTableFilter}
					/>
				</StyledGridItemTables>
				<StyledGridItemRight>
					{
						warnings && (
							<CustomerWarnings warnings={warnings} />
						)
					}
					<StyledEventsTab
						$endDiv
						$fullHeight
						$isSmall={isSmallScreen}
						ref={sectionRef}
						expandEvent={expandEvent}
						style={{
							paddingBottom: expandEvent ? `${boundingRect?.top}px` : '0',
							minHeight: isSmallScreenHeight ? '100px' : undefined,
						}}
					>
						<EventsTab
							expEvent={expandEvent}
							onShowExpand={onShowExpand}
							onHideExpand={onHideExpand}
							customerId={customerId ? customerId : ''}
						/>
					</StyledEventsTab>
				</StyledGridItemRight>
			</StyledCustomerGrid>
			<Dialog
				show={showZendeskDialog}
				onClose={() => setShowZendeskDialog(false)}
				title={t('Create Zendesk ticket')}
			>
				<CreateZendeskContent
					requesterId={123456}
					assigneeId={777}
					groupId={222}
				/>
			</Dialog>
			<PageLoader />
		</Page>
	);
}

export default ThreeSixtyCustomer;
